import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

type Tenant = {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    primary_color: string | null;
    subscription_tier: string | null;
};

type TenantContextValue = {
    tenant: Tenant | null;
    loading: boolean;
    notFound: boolean;
    isCareSubdomain: boolean;
};

const TenantContext = createContext<TenantContextValue>({
    tenant: null,
    loading: true,
    notFound: false,
    isCareSubdomain: false,
});

function extractCareSubdomain(hostname: string): string | null {
    // Matches: {slug}.care.dipps.ca
  const match = hostname.match(/^([a-z0-9-]+)\.care\.dipps\.ca$/);
    return match ? match[1] : null;
}

export function TenantProvider({ children }: { children: ReactNode }) {
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [isCareSubdomain, setIsCareSubdomain] = useState(false);

  useEffect(() => {
        const hostname = window.location.hostname;
        const subdomainSlug = extractCareSubdomain(hostname);

                // Not a care.dipps.ca subdomain - exit early, app works as before
                if (!subdomainSlug) {
                        setLoading(false);
                        return;
                }

                setIsCareSubdomain(true);

                // Look up tenant by slug
                if (!supabase) {
                        setLoading(false);
                        return;
                }

                supabase
          .from('tenants')
          .select('id, name, slug, logo_url, primary_color, subscription_tier')
          .eq('slug', subdomainSlug)
          .maybeSingle()
          .then(({ data, error }) => {
                    if (error) {
                                console.error('[TenantContext] Supabase error:', error);
                                setNotFound(true);
                    } else if (!data) {
                                setNotFound(true);
                    } else {
                                setTenant(data);
                    }
                    setLoading(false);
          });
  }, []);

  return (
        <TenantContext.Provider value={{ tenant, loading, notFound, isCareSubdomain }}>
          {children}
        </TenantContext.Provider>
      );
}

export function useTenant(): TenantContextValue {
    return useContext(TenantContext);
}
